import { useCallback, useEffect } from "react";
import type { MutableRefObject } from "react";
import type { MappingResultRow } from "@models/mappingResult";

interface UseResultsKeyboardShortcutsArgs {
  rows: MappingResultRow[];
  focusedRowIndex: number | null;
  setFocusedRowIndex: (index: number | null) => void;
  rowRefs: MutableRefObject<Map<number, HTMLTableRowElement | null>>;
  onAccept: (rowIndex: number) => void;
  onReject: (rowIndex: number) => void;
}

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
};

// Keyboard shortcuts for the results list:
//   J / ArrowDown   move focus down
//   K / ArrowUp     move focus up
//   A               accept top match on focused row
//   R               reject focused row
// Skips when the user is typing in an input / textarea / contenteditable.
export const useResultsKeyboardShortcuts = ({
  rows,
  focusedRowIndex,
  setFocusedRowIndex,
  rowRefs,
  onAccept,
  onReject,
}: UseResultsKeyboardShortcutsArgs): void => {
  const focusRowAt = useCallback(
    (rowIndex: number): void => {
      setFocusedRowIndex(rowIndex);
      const el = rowRefs.current.get(rowIndex);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    },
    [rowRefs, setFocusedRowIndex],
  );

  // Reset focus when the visible row set changes (filter / new data).
  useEffect(() => {
    if (rows.length === 0) {
      setFocusedRowIndex(null);
      return;
    }
    if (
      focusedRowIndex === null ||
      !rows.some((r) => r.rowIndex === focusedRowIndex)
    ) {
      setFocusedRowIndex(rows[0].rowIndex);
    }
  }, [rows, focusedRowIndex, setFocusedRowIndex]);

  useEffect(() => {
    if (rows.length === 0) return;

    const handleKey = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (focusedRowIndex === null) return;

      const currentIdx = rows.findIndex(
        (r) => r.rowIndex === focusedRowIndex,
      );
      if (currentIdx === -1) return;
      const focusedRow = rows[currentIdx];

      switch (event.key) {
        case "j":
        case "ArrowDown": {
          event.preventDefault();
          const next = rows[Math.min(currentIdx + 1, rows.length - 1)];
          focusRowAt(next.rowIndex);
          return;
        }
        case "k":
        case "ArrowUp": {
          event.preventDefault();
          const prev = rows[Math.max(currentIdx - 1, 0)];
          focusRowAt(prev.rowIndex);
          return;
        }
        case "a":
        case "A":
          if (focusedRow.target) {
            event.preventDefault();
            onAccept(focusedRow.rowIndex);
          }
          return;
        case "r":
        case "R":
          event.preventDefault();
          onReject(focusedRow.rowIndex);
          return;
        default:
          break;
      }

    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [rows, focusedRowIndex, focusRowAt, onAccept, onReject]);
};
